import { useCallback, useEffect, useId, useRef, useState, type ReactNode } from 'react';
import { escapeRich, richToHtml, runsToRich, type RichLine, type RichRun } from '@tww/shared';
import { Field } from './fields';
import '../styles/richtext.css';

/**
 * Textfeld mit Formatierung für E-Mail-Texte: fett, kursiv, unterstrichen, Aufzählung und nummerierte Liste
 * (Werkzeugleiste und Strg+B/I/U). Wert ist das gespeicherte Format aus `@tww/shared` (richText.ts), nicht HTML.
 */
export function RichTextArea({ value, onChange, rows = 8, disabled, label, hint, error, required, full = true, className }: { value: string | null | undefined; onChange: (v: string) => void; rows?: number; disabled?: boolean; label?: ReactNode; hint?: ReactNode; error?: string; required?: boolean; full?: boolean; className?: string }) {
  const id = useId();
  const ref = useRef<HTMLDivElement>(null);
  const last = useRef<string | null>(null);
  const [state, setState] = useState({ b: false, i: false, u: false, ul: false, ol: false });

  // Von außen gesetzten Wert übernehmen, eigene Eingaben nicht zurückschreiben (sonst springt der Cursor)
  useEffect(() => {
    const el = ref.current;
    if (!el || (value ?? '') === last.current) return;
    el.innerHTML = richToHtml(value ?? '');
    last.current = value ?? '';
  }, [value]);

  const emit = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    const md = runsToRich(readLines(el));
    last.current = md;
    // nur echte Änderungen melden: „· “ statt „- “ o. Ä. im gespeicherten Text macht den Entwurf nicht geändert
    if (md !== (value ?? '') && md !== normalize(value ?? '')) onChange(md);
  }, [onChange, value]);

  const refreshState = useCallback(() => {
    const el = ref.current;
    const sel = document.getSelection();
    if (!el || !sel?.anchorNode || !el.contains(sel.anchorNode)) return;
    setState({ b: document.queryCommandState('bold'), i: document.queryCommandState('italic'), u: document.queryCommandState('underline'), ul: document.queryCommandState('insertUnorderedList'), ol: document.queryCommandState('insertOrderedList') });
  }, []);
  useEffect(() => {
    document.addEventListener('selectionchange', refreshState);
    return () => document.removeEventListener('selectionchange', refreshState);
  }, [refreshState]);

  const cmd = (c: string) => {
    if (disabled) return;
    ref.current?.focus();
    document.execCommand('styleWithCSS', false, 'false');
    document.execCommand(c);
    emit();
    refreshState();
  };

  const tools: { c: string; k: keyof typeof state; label: string; icon: ReactNode }[] = [
    { c: 'bold', k: 'b', label: 'Fett (Strg+B)', icon: <b>F</b> },
    { c: 'italic', k: 'i', label: 'Kursiv (Strg+I)', icon: <i style={{ fontFamily: 'Georgia, serif' }}>K</i> },
    { c: 'underline', k: 'u', label: 'Unterstrichen (Strg+U)', icon: <u>U</u> },
    { c: 'insertUnorderedList', k: 'ul', label: 'Aufzählung', icon: <ListIcon /> },
    { c: 'insertOrderedList', k: 'ol', label: 'Nummerierte Liste', icon: <ListIcon numbered /> },
  ];

  return (
    <Field label={label} hint={hint} error={error} required={required} full={full} className={`rte-field ${className ?? ''}`} htmlFor={id}>
      <div className={`rte ${disabled ? 'disabled' : ''}`}>
        {!disabled && (
          <div className="rte-bar" role="toolbar" aria-label="Formatierung">
            {tools.map((t, n) => (
              <button
                key={t.c}
                type="button"
                className={`rte-btn ${state[t.k] ? 'on' : ''} ${n === 3 ? 'sep' : ''}`}
                title={t.label}
                aria-label={t.label}
                aria-pressed={state[t.k]}
                // Fokus und Auswahl im Text behalten
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => cmd(t.c)}
              >
                {t.icon}
              </button>
            ))}
          </div>
        )}
        <div
          id={id}
          ref={ref}
          className="rte-body"
          role="textbox"
          aria-multiline="true"
          aria-label={typeof label === 'string' ? label : undefined}
          aria-disabled={disabled || undefined}
          contentEditable={!disabled}
          suppressContentEditableWarning
          style={{ minHeight: `${rows * 1.5 + 1.2}em` }}
          onInput={emit}
          onKeyDown={(e) => {
            if (!(e.ctrlKey || e.metaKey) || e.altKey) return;
            const k = e.key.toLowerCase();
            const c = k === 'b' ? 'bold' : k === 'i' ? 'italic' : k === 'u' ? 'underline' : null;
            if (c) (e.preventDefault(), cmd(c));
          }}
          onPaste={(e) => {
            // Nur Text einfügen; Zeilen mit „- “ oder „1. “ werden dabei zu Listen
            e.preventDefault();
            const text = e.clipboardData.getData('text/plain');
            const lines = text.replace(/\r\n?/g, '\n').split('\n');
            const md = lines.map((l) => (/^\s*([-·•*]|\d+[.)])\s+/.test(l) ? l.replace(/^\s*\*\s+/, '- ').replace(/^(\s*[-·•]\s+|\s*\d+[.)]\s+)(.*)$/, (_m, p: string, rest: string) => p + escapeRich(rest)) : escapeRich(l))).join('\n');
            const html = lines.length > 1 ? richToHtml(md) : richToHtml(md).replace(/^<div>|<\/div>$/g, '');
            document.execCommand('insertHTML', false, html);
            emit();
          }}
        />
      </div>
    </Field>
  );
}

function ListIcon({ numbered }: { numbered?: boolean }) {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" aria-hidden="true" fill="currentColor">
      {numbered ? (
        <>
          <text x="0" y="5.5" fontSize="5.5" fontWeight="600">1</text>
          <text x="0" y="10.5" fontSize="5.5" fontWeight="600">2</text>
          <text x="0" y="15.5" fontSize="5.5" fontWeight="600">3</text>
        </>
      ) : (
        <>
          <circle cx="2" cy="3.5" r="1.4" />
          <circle cx="2" cy="8" r="1.4" />
          <circle cx="2" cy="12.5" r="1.4" />
        </>
      )}
      <rect x="5.5" y="2.8" width="10" height="1.5" rx=".7" />
      <rect x="5.5" y="7.3" width="10" height="1.5" rx=".7" />
      <rect x="5.5" y="11.8" width="10" height="1.5" rx=".7" />
    </svg>
  );
}

/** Gespeicherten Text so normalisieren, wie ihn der Editor zurückgeben würde */
function normalize(md: string): string {
  const div = document.createElement('div');
  div.innerHTML = richToHtml(md);
  return runsToRich(readLines(div));
}

type Line = { kind: RichLine['kind']; runs: RichRun[] };
type Marks = { b: boolean; i: boolean; u: boolean };

const BLOCK = new Set(['DIV', 'P', 'H1', 'H2', 'H3', 'H4', 'H5', 'H6', 'BLOCKQUOTE', 'PRE', 'SECTION', 'ARTICLE', 'HEADER', 'FOOTER', 'TABLE', 'TR']);

/** Inhalt des Editors in Zeilen mit formatierten Textstücken zerlegen (Gegenstück zu richToHtml) */
function readLines(root: HTMLElement): Line[] {
  const lines: Line[] = [];
  let cur: Line | null = null;
  let inList: 'ul' | 'ol' | null = null;
  const line = (): Line => (cur ??= { kind: 'p', runs: [] });
  const flush = (keepEmpty: boolean) => {
    if (cur) lines.push(cur);
    else if (keepEmpty) lines.push({ kind: 'p', runs: [] });
    cur = null;
  };
  const marksOf = (el: HTMLElement, m: Marks): Marks => {
    const t = el.tagName;
    const st = el.style;
    const w = st.fontWeight;
    return {
      b: t === 'B' || t === 'STRONG' || (w ? w === 'bold' || Number(w) >= 600 : m.b),
      i: t === 'I' || t === 'EM' || (st.fontStyle ? st.fontStyle === 'italic' : m.i),
      u: t === 'U' || (st.textDecorationLine || st.textDecoration || '').includes('underline') || m.u,
    };
  };
  const walk = (node: Node, m: Marks) => {
    if (node.nodeType === Node.TEXT_NODE) {
      const text = (node.textContent ?? '').replace(/ /g, ' ').replace(/​/g, '');
      if (text) line().runs.push({ text, ...m });
      return;
    }
    if (!(node instanceof HTMLElement)) return;
    const t = node.tagName;
    if (t === 'BR') {
      // abschließendes <br> in einem Block ist nur Platzhalter für eine leere Zeile
      if (inList) line().runs.push({ text: ' ' });
      else if (node.nextSibling) flush(true);
      return;
    }
    if (t === 'UL' || t === 'OL') {
      flush(false);
      const prev = inList;
      inList ??= t === 'UL' ? 'ul' : 'ol';
      for (const c of node.childNodes) walk(c, m);
      inList = prev;
      if (!prev) flush(false);
      return;
    }
    if (t === 'LI') {
      flush(false);
      cur = { kind: inList ?? 'ul', runs: [] };
      for (const c of node.childNodes) walk(c, marksOf(node, m));
      if (cur) lines.push(cur);
      else lines.push({ kind: inList ?? 'ul', runs: [] });
      cur = null;
      return;
    }
    const mm = marksOf(node, m);
    if (BLOCK.has(t) && !inList) {
      flush(false);
      const before = lines.length;
      for (const c of node.childNodes) walk(c, mm);
      if (cur) flush(false);
      else if (lines.length === before) lines.push({ kind: 'p', runs: [] });
      return;
    }
    for (const c of node.childNodes) walk(c, mm);
  };
  for (const c of root.childNodes) walk(c, { b: false, i: false, u: false });
  flush(false);
  return lines;
}
